import _ from 'lodash'
import React from 'react'

import { PaginationDetails } from 'constants/pagination'
import { ChildBooking } from 'services/booking/childBooking/constants'
import { Child } from 'services/child/constants'
import { AGE_IN_MONTHS_OPTIONS } from 'services/legacy/child/constants'
import { NurseryFundingProductType } from 'services/product/nurseryFundingV3/constants'

import { generateRoute } from 'utils/routing'

import {
  Button,
  Callout,
  DatePicker,
  EmptyState,
  Form,
  InfiniteDropdowns,
  Page,
  Pagination,
  SearchBar,
  Section,
  Select,
  Space,
  Spinner,
  Toolbar,
} from 'components'

import i18n from 'translations'

import RegularBookingsListItem from './components/RegularBookingsListItem'
import { RegularBookingsListFilters } from './RegularBookingsListContainer'

interface RegularBookingsListViewProps {
  child: Child
  errorMessages: string[]
  filters: RegularBookingsListFilters
  hasAccessToEditRegularBooking: boolean
  isChildContext: boolean
  isFetching: boolean
  onFilterChange: <K extends keyof RegularBookingsListFilters>(key: K) => (value: RegularBookingsListFilters[K]) => void
  onSearchChange?: (search: string) => void
  pagination: PaginationDetails
  records: ChildBooking[]
  search?: string
}

const RegularBookingsListView: React.FC<RegularBookingsListViewProps> = ({
  child,
  errorMessages,
  filters,
  hasAccessToEditRegularBooking,
  isChildContext,
  isFetching,
  onFilterChange,
  onSearchChange,
  pagination,
  records,
  search,
}) => {
  const renderPagination = () => !isFetching && (
    <Pagination {...pagination} />
  )

  const renderFilters = () => (
    <Toolbar>
      <Toolbar.Group>
        <Toolbar.Item>
          <Form.Row label={i18n.t('module:Finance:RegularBookings:List:planActiveOn')} verticalLabel>
            <DatePicker value={filters.planActiveOn} clearable onChange={onFilterChange('planActiveOn')} />
          </Form.Row>
        </Toolbar.Item>
        {!isChildContext && (
          <Toolbar.Item>
            <Form.Row label={i18n.t('global:Room')} verticalLabel>
              <InfiniteDropdowns.Rooms
                disabled={!filters.planActiveOn}
                placeholder={i18n.t('global:Room')}
                value={filters.room}
                v2
                onChange={onFilterChange('room')}
              />
            </Form.Row>
          </Toolbar.Item>
        )}
        <Toolbar.Item>
          <Form.Row label={i18n.t('module:Finance:RegularBookings:List:fundingType')} verticalLabel>
            <InfiniteDropdowns.NurseryFundingsV3
              criteria={[{
                field: 'type',
                value: [
                  NurseryFundingProductType.NURSERY_REGULAR_FUNDING,
                ],
              }]}
              placeholder={i18n.t('module:Finance:RegularBookings:List:fundingType')}
              value={filters.fundingType}
              v2
              onChange={onFilterChange('fundingType')}
            />
          </Form.Row>
        </Toolbar.Item>
        {!isChildContext && (
          <React.Fragment>
            <Toolbar.Item>
              <Form.Row label={i18n.t('module:Finance:RegularBookings:List:startAgeInMonths')} verticalLabel>
                <Select
                  options={AGE_IN_MONTHS_OPTIONS}
                  placeholder={i18n.t('module:Finance:RegularBookings:List:startAgeInMonths')}
                  value={filters.startAge}
                  v2
                  onChange={onFilterChange('startAge')}
                />
              </Form.Row>
            </Toolbar.Item>
            <Toolbar.Item>
              <Form.Row label={i18n.t('module:Finance:RegularBookings:List:endAgeInMonths')} verticalLabel>
                <Select
                  options={AGE_IN_MONTHS_OPTIONS}
                  placeholder={i18n.t('module:Finance:RegularBookings:List:endAgeInMonths')}
                  value={filters.endAge}
                  v2
                  onChange={onFilterChange('endAge')}
                />
              </Form.Row>
            </Toolbar.Item>
          </React.Fragment>
        )}
      </Toolbar.Group>
    </Toolbar>
  )

  const renderContent = () => {
    if (isFetching) {
      return <Spinner />
    }

    if (!isFetching && !records?.length) {
      return (
        <EmptyState
          actions={isChildContext && hasAccessToEditRegularBooking && (
            <Button
              label={i18n.t('module:Finance:RegularBookings:List:createFirstRegularBooking')}
              to={generateRoute('CHILDREN.CHILD.BOOKING_PATTERN.REGULAR_BOOKINGS.ADD', {
                childId: child?.id,
              })}
            />
          )}
          icon="time"
          text1={isChildContext
            ? i18n.t('module:Finance:RegularBookings:List:notFoundChildContext', {
              childName: `${child.firstName} ${child.surname}`,
            })
            : i18n.t('module:Finance:RegularBookings:List:notFound')}
        />
      )
    }

    return (
      <div>
        {renderPagination()}
        <Space space="15px" />
        {_.map(records, (record) => (
          <RegularBookingsListItem
            childBooking={record}
            hasAccessToEditRegularBooking={hasAccessToEditRegularBooking}
            isChildContext={isChildContext}
            key={record?.id}
          />
        ))}
        {renderPagination()}
      </div>
    )
  }

  const actions = (
    <Section.Actions
      primary={[{
        to: generateRoute('CHILDREN.CHILD.BOOKING_PATTERN.REGULAR_BOOKINGS.ADD', {
          childId: child?.id,
        }),
      }]}
    />
  )

  return (
    <React.Fragment>
      {!isChildContext && (
        <SearchBar
          placeholder={i18n.t('module:Finance:OneOffBookings:Filters:Search:label')}
          value={search}
          onChange={onSearchChange}
        />
      )}
      <Page.Section
        actions={isChildContext && hasAccessToEditRegularBooking && actions}
        isLoading={isFetching}
        title={i18n.t('module:Finance:RegularBookings:title')}
      >
        <Callout content={errorMessages} error />
        {renderFilters()}
        {renderContent()}
      </Page.Section>
    </React.Fragment>
  )
}

export default RegularBookingsListView
